import { IconName } from '@fortawesome/fontawesome-svg-core';
import type { TFunction } from 'i18next';
import React, { ReactNode } from 'react';
import { DefaultTheme } from 'styled-components';
import { v4 } from 'uuid';

import { isDefined, isIncluded } from '../../../../utils/variables';
import { Icon } from '../../icon/icon';
import { LocalGeodataImage } from './types';

function resolveColor(image: LocalGeodataImage, theme: DefaultTheme) {
  if (!image.enabled) {
    return theme.color.gray.medium;
  }

  if (image.status === 'COMPLETED') {
    return isDefined(image.file) ? theme.color.green : theme.color.blue;
  }

  if (image.status === 'UPLOADING') {
    return theme.color.blue;
  }

  if (image.status === 'FAILED' || image.status === 'UPLOAD_FAILED') {
    return theme.color.red;
  }

  return theme.color.gray.medium;
}

function resolveIconName(image: LocalGeodataImage): IconName {
  if (!image.enabled) {
    return 'circle-xmark';
  }

  if (image.status === 'COMPLETED') {
    return 'check-circle';
  }

  if (image.status === 'PENDING') {
    return 'spinner';
  }

  if (image.status === 'UPLOADING' || image.status === 'UPLOAD_FAILED') {
    return 'upload';
  }

  if (isIncluded(['FAILED'], image.status)) {
    return 'triangle-exclamation';
  }

  return 'question-circle';
}

function resolveTitle(image: LocalGeodataImage, t: TFunction) {
  if (!image.enabled) {
    return t('disabled', { ns: 'common' });
  }

  if (image.status === 'COMPLETED') {
    return isDefined(image.file)
      ? t('imageList.statusIcon.finishedProcessingAndLinked', { ns: 'cloudProcessing' })
      : t('imageList.statusIcon.finishedProcessing', { ns: 'cloudProcessing' });
  }

  if (image.status === 'UPLOADING') {
    return t('imageList.statusIcon.uploadingImage', { ns: 'cloudProcessing' });
  }

  if (image.status === 'FAILED') {
    return t('imageList.statusIcon.invalidImage', { ns: 'cloudProcessing' });
  }

  if (image.status === 'FAILED_MISSING_GPS_DATA') {
    return t('imageList.statusIcon.missingGpsData', { ns: 'cloudProcessing' });
  }

  if (image.status === 'FAILED_MISSING_CAPTURE_DATE') {
    return t('imageList.statusIcon.missingCaptureDate', { ns: 'cloudProcessing' });
  }

  if (image.status === 'UPLOAD_FAILED') {
    return t('imageList.statusIcon.uploadFailed', { ns: 'cloudProcessing' });
  }

  return t('imageList.statusIcon.preparingImage', { ns: 'cloudProcessing' });
}

export function GeodataImageStatusIcon(
  image: LocalGeodataImage,
  theme: DefaultTheme,
  t: TFunction,
): ReactNode {
  return (
    <Icon
      beatFade={image.status === 'UPLOADING'}
      color={resolveColor(image, theme)}
      fixedWidth={true}
      icon={['fad', resolveIconName(image)]}
      key={v4()}
      spin={image.status === 'PENDING'}
      title={resolveTitle(image, t)}
      onHoverStyle={image.status === 'UPLOAD_FAILED' ? { icon: ['fad', 'refresh'] } : undefined}
    />
  );
}

export function GeodataImageUseGpsIcon(image: LocalGeodataImage, theme: DefaultTheme): ReactNode {
  return (
    <Icon
      color={image.useGps ? theme.color.brand.dark : theme.color.gray.light}
      fixedWidth={true}
      icon={['fas', 'location-dot']}
      key={v4()}
      title={image.useGps ? 'GPS position används' : 'GPS position inaktiverad'}
    />
  );
}
